/**
 * Submitting a document: the details, and who is being asked to look at it.
 *
 * The people are the point. Before this, a submitted document was routed to
 * whichever manager had the fewest open approvals, and the same lookup chose
 * the reviewer — so an author never knew who would read their work, and the
 * "final approver" was frequently the person who had just reviewed it.
 *
 * One route serves the first submission and every resubmission after it, so
 * the loop back through review needs no second form. On a resubmit the roster
 * is kept unless it is changed, because a change request usually goes back to
 * the same people.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  api,
  type AssignableUser,
  type AssignableUsers,
  type AssignmentsResponse,
  type DocumentDetail,
} from "../../lib/api";
import {
  Avatar,
  Banner,
  Card,
  ErrorNote,
  PageHeader,
  Spinner,
  useToast,
} from "../../components/ui";

function PersonRow({
  person,
  checked,
  onToggle,
  kind,
  disabled,
  disabledReason,
}: {
  person: AssignableUser;
  checked: boolean;
  onToggle: () => void;
  kind: "checkbox" | "radio";
  disabled?: boolean;
  disabledReason?: string;
}) {
  return (
    <li>
      <label
        className={`flex items-center gap-3 border-b border-line-soft px-3 py-2.5 last:border-0 ${
          disabled ? "opacity-45" : "cursor-pointer hover:bg-surface-hover"
        }`}
        title={disabled ? disabledReason : undefined}
      >
        <input
          type={kind}
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
          className="h-3.5 w-3.5 shrink-0 accent-accent-deep"
        />
        <Avatar
          initials={person.initials}
          colour={person.avatarColour}
          size={26}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-base font-bold text-ink">
            {person.displayName}
          </span>
          <span className="block truncate text-2xs text-muted-soft">
            {person.roleLabel} · {person.scopeLabel}
          </span>
        </span>
        <span className="shrink-0 text-2xs text-muted-soft">
          {person.openAssignments === 0
            ? "free"
            : `${person.openAssignments} waiting`}
        </span>
      </label>
    </li>
  );
}

export function SubmitForReviewScreen() {
  const { documentId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const document = useQuery({
    queryKey: ["document", documentId],
    queryFn: () => api.get<DocumentDetail>(`/documents/${documentId}`),
  });
  const people = useQuery({
    queryKey: ["assignable", documentId],
    queryFn: () =>
      api.get<AssignableUsers>("/workflow/assignable-users", {
        document_id: documentId,
      }),
  });
  const existing = useQuery({
    queryKey: ["assignments", documentId],
    queryFn: () =>
      api.get<AssignmentsResponse>(`/workflow/documents/${documentId}/assignments`),
  });

  const [reviewerIds, setReviewerIds] = useState<string[]>([]);
  const [approverId, setApproverId] = useState<string | null>(null);
  const [changeNote, setChangeNote] = useState("");
  const [comment, setComment] = useState("");
  const [touched, setTouched] = useState(false);

  // Start from whoever was named last time, or from the suggested approver on
  // a first submission. Only until the author touches the form — after that
  // their choice stands, and a refetch must not undo it.
  useEffect(() => {
    if (touched || !people.data) return;
    if (existing.data && existing.data.reviewers.length > 0) {
      setReviewerIds(existing.data.reviewers.map((r) => r.userId));
      setApproverId(existing.data.approver?.userId ?? null);
    } else if (people.data.suggestedApproverId) {
      setApproverId(people.data.suggestedApproverId);
    }
  }, [people.data, existing.data, touched]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>(
        `/workflow/documents/${documentId}/submit`,
        {
          reviewerIds,
          approverId,
          changeNote: changeNote.trim() || undefined,
          comment: comment.trim() || undefined,
        },
      ),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["document", documentId] });
      void queryClient.invalidateQueries({ queryKey: ["board"] });
      navigate(`/documents/${documentId}`);
    },
    onError: (caught) =>
      toast(
        caught instanceof Error ? caught.message : "Could not submit",
        "danger",
      ),
  });

  if (document.isLoading || people.isLoading) return <Spinner />;
  if (document.error) return <ErrorNote error={document.error} />;
  if (people.error) return <ErrorNote error={people.error} />;
  if (!document.data || !people.data) return null;

  const ready = reviewerIds.length > 0 && approverId !== null;

  return (
    <>
      <PageHeader
        eyebrow="Submit for review"
        title={document.data.title}
        subtitle={
          <>
            {document.data.docNo} · {document.data.versionLabel} ·{" "}
            <Link to={`/documents/${documentId}`}>Back to the document</Link>
          </>
        }
      />

      {people.data.reviewers.length === 0 && (
        <Banner tone="warn">
          Nobody can be named as a reviewer for this document. A reviewer has to
          be able to see a draft in this category — check the category, or ask
          an administrator to widen someone&rsquo;s scope.
        </Banner>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card
          title="Reviewers"
          meta={`${reviewerIds.length} chosen`}
          padded={false}
        >
          <p className="border-b border-line-soft px-3 py-2 text-2xs text-muted">
            Everyone chosen is notified, and the document stays in review until
            all of them have answered.
          </p>
          <ul className="max-h-[360px] overflow-y-auto overscroll-contain">
            {people.data.reviewers.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                kind="checkbox"
                checked={reviewerIds.includes(person.id)}
                disabled={person.id === approverId}
                disabledReason="Already chosen as the approver — the second look is the point."
                onToggle={() => {
                  setTouched(true);
                  setReviewerIds((current) =>
                    current.includes(person.id)
                      ? current.filter((id) => id !== person.id)
                      : [...current, person.id],
                  );
                }}
              />
            ))}
          </ul>
        </Card>

        <Card title="Final approver" meta="exactly one" padded={false}>
          <p className="border-b border-line-soft px-3 py-2 text-2xs text-muted">
            Looks at the document once every reviewer has approved, and is the
            only person who can publish it.
          </p>
          <ul className="max-h-[360px] overflow-y-auto overscroll-contain">
            {people.data.approvers.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                kind="radio"
                checked={approverId === person.id}
                disabled={reviewerIds.includes(person.id)}
                disabledReason="Already chosen as a reviewer."
                onToggle={() => {
                  setTouched(true);
                  setApproverId(person.id);
                }}
              />
            ))}
          </ul>
        </Card>
      </div>

      <Card className="mt-4" title="Notes">
        <label className="mb-3 block">
          <span className="eyebrow mb-1.5 block !text-muted">
            What changed in this version
          </span>
          <input
            className="field"
            value={changeNote}
            onChange={(event) => setChangeNote(event.target.value)}
            placeholder="Route order corrected in section 4.2"
          />
        </label>
        <label className="block">
          <span className="eyebrow mb-1.5 block !text-muted">
            A note to the reviewers (optional)
          </span>
          <textarea
            className="field"
            rows={3}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </label>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            className="btn-accent"
            disabled={!ready || submit.isPending}
            onClick={() => submit.mutate()}
            title={
              ready
                ? undefined
                : "Choose at least one reviewer and an approver."
            }
          >
            {submit.isPending ? "Submitting…" : "Submit for review"}
          </button>
          <Link to={`/documents/${documentId}`} className="btn-ghost">
            Cancel
          </Link>
          {existing.data && existing.data.roundNo > 0 && (
            <span className="text-2xs text-muted-soft">
              Round {existing.data.roundNo + 1}. Everyone&rsquo;s decision
              starts again, because the text they approved has changed.
            </span>
          )}
        </div>
      </Card>
    </>
  );
}
